import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import { 
  HardDrive, Cloud, Upload, Download, Trash2, AlertTriangle, 
  ShoppingCart, FileText, Image, Archive, RefreshCw, CreditCard,
  Database, Calendar, RotateCcw, Eye, ChevronRight, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

// Archivable collections with friendly names
const COLLECTION_NAMES = {
  jobs: 'Jobs',
  notifications: 'Notifications',
  notification_logs: 'Notification Logs',
  audit_log: 'Audit Log',
  time_entries: 'Time Entries',
  driver_locations: 'Driver Locations',
  driver_status_logs: 'Driver Status Logs',
  tracking_sessions: 'Tracking Sessions',
  chat_messages: 'Chat Messages',
  message_logs: 'Message Logs',
  emergency_alerts: 'Emergency Alerts',
  qr_codes: 'QR Codes',
  inventory_items: 'Inventory Items',
};

const StorageManagement = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('storage');
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);
  const [files, setFiles] = useState([]);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseAmount, setPurchaseAmount] = useState(5);
  const [pricing, setPricing] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Archive state
  const [archiveStats, setArchiveStats] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [archivedDocuments, setArchivedDocuments] = useState([]);
  const [viewingArchive, setViewingArchive] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedForRestore, setSelectedForRestore] = useState([]);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'archive') {
      fetchArchiveStats();
    }
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usageRes, filesRes, pricingRes] = await Promise.all([
        api.get('/storage/usage'),
        api.get('/storage/files'),
        api.get('/storage/pricing')
      ]);
      
      setUsage(usageRes.data);
      setFiles(filesRes.data.files || []);
      setPricing(pricingRes.data);
    } catch (error) {
      console.error('Error fetching storage data:', error);
      toast.error('Failed to load storage information');
    } finally {
      setLoading(false);
    }
  };

  const fetchArchiveStats = async () => {
    setArchiveLoading(true);
    try {
      const response = await api.get('/archive/stats');
      setArchiveStats(response.data);
    } catch (error) {
      console.error('Error fetching archive stats:', error);
      toast.error('Failed to load archive statistics');
    } finally {
      setArchiveLoading(false);
    }
  };

  const fetchArchivedData = async (collection) => {
    if (!collection) return;
    
    setViewingArchive(true);
    try {
      const response = await api.get(`/archive/data/${collection}`);
      setArchivedDocuments(response.data.documents || []);
      setSelectedCollection(collection);
    } catch (error) {
      console.error('Error fetching archived data:', error);
      toast.error('Failed to load archived data');
      setArchivedDocuments([]);
    } finally {
      setViewingArchive(false);
    }
  };

  const handleRestore = async () => {
    if (selectedForRestore.length === 0) {
      toast.error('Please select documents to restore');
      return;
    }

    setRestoring(true);
    try {
      const response = await api.post('/archive/restore', null, {
        params: {
          collection: selectedCollection,
          document_ids: selectedForRestore
        }
      });
      
      toast.success(`Restored ${response.data.restored} documents`);
      setRestoreDialogOpen(false);
      setSelectedForRestore([]);
      fetchArchivedData(selectedCollection);
      fetchArchiveStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore documents');
    } finally {
      setRestoring(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 50MB.');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', 'documents');

    try {
      const response = await api.post('/storage/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('File uploaded successfully');
      
      if (response.data.warning) {
        toast.warning(response.data.warning.message);
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (filePath) => {
    if (!confirm('Are you sure you want to delete this file?')) return;

    try {
      await api.delete(`/storage/files/${encodeURIComponent(filePath)}`);
      toast.success('File deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handlePurchase = async () => {
    try {
      const response = await api.post('/storage/purchase', null, {
        params: { gb_amount: purchaseAmount }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.success(`Successfully added ${purchaseAmount}GB storage!`);
        setShowPurchaseModal(false);
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed');
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) return <Image className="h-4 w-4" />;
    if (contentType?.includes('pdf')) return <FileText className="h-4 w-4" />;
    return <Archive className="h-4 w-4" />;
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="storage-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cloud Storage</h1>
          <p className="text-gray-500">Manage your company&apos;s storage and archived data</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="storage" className="flex items-center gap-2">
            <Cloud className="h-4 w-4" />
            Storage
          </TabsTrigger>
          <TabsTrigger value="archive" className="flex items-center gap-2">
            <Archive className="h-4 w-4" />
            Archive
          </TabsTrigger>
        </TabsList>

        {/* Storage Tab */}
        <TabsContent value="storage" className="space-y-6">
          {/* Actions */}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <label>
              <Button disabled={uploading}>
                <Upload className="mr-2 h-4 w-4" />
                {uploading ? 'Uploading...' : 'Upload File'}
              </Button>
              <input 
                type="file" 
                className="hidden" 
                onChange={handleFileUpload}
                disabled={uploading}
              />
            </label>
          </div>

          {/* Storage Usage Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Cloud className="h-5 w-5 text-blue-500" />
                  <CardTitle>Storage Usage</CardTitle>
                </div>
                {usage?.percentage_used >= 80 && (
                  <Badge variant="destructive" className="flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {usage.percentage_used >= 100 ? 'Limit Reached' : 'Running Low'}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{usage?.used_gb} GB used</span>
                  <span>{usage?.limit_gb} GB limit</span>
                </div>
                <Progress 
                  value={usage?.percentage_used || 0} 
                  className={`h-3 ${getUsageColor(usage?.percentage_used)}`}
                />
                <p className="text-sm text-gray-500">
                  {usage?.percentage_used}% of storage used
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{usage?.base_limit_gb} GB</p>
                  <p className="text-sm text-gray-500">Plan Included</p>
                  <Badge variant="outline" className="mt-1">{usage?.plan_type}</Badge>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{usage?.purchased_gb || 0} GB</p>
                  <p className="text-sm text-gray-500">Additional</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">
                    {(usage?.limit_gb - usage?.used_gb).toFixed(2)} GB
                  </p>
                  <p className="text-sm text-gray-500">Available</p>
                </div>
              </div>

              <div className="flex justify-center pt-4">
                <Button 
                  onClick={() => setShowPurchaseModal(true)}
                  className="bg-gradient-to-r from-blue-500 to-purple-500"
                >
                  <ShoppingCart className="mr-2 h-4 w-4" />
                  Buy More Storage
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Files List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HardDrive className="h-5 w-5" />
                Your Files ({files.length})
              </CardTitle>
              <CardDescription>
                Files are encrypted and stored securely in the cloud
              </CardDescription>
            </CardHeader>
            <CardContent>
              {files.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Cloud className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No files uploaded yet</p>
                  <p className="text-sm">Upload files to see them here</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {files.map((file, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-3">
                        {getFileIcon(file.content_type)}
                        <div>
                          <p className="font-medium text-sm">{file.file_path.split('/').pop()}</p>
                          <p className="text-xs text-gray-500">
                            {formatBytes(file.original_size)} • {file.storage_class}
                            {file.archived && <Badge variant="secondary" className="ml-2">Archived</Badge>}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => window.open(`/api/storage/download/${encodeURIComponent(file.file_path)}`, '_blank')}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(file.file_path)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Archive Tab */}
        <TabsContent value="archive" className="space-y-6">
          {/* Archive Info Banner */}
          <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Archive className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-900">Data Archive System</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    Data older than 30 days is automatically archived to Google Cloud Storage. 
                    This keeps your live database fast while preserving all historical records.
                    Archived data can be retrieved on demand or restored back to active database.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Archive Stats */}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={fetchArchiveStats} disabled={archiveLoading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${archiveLoading ? 'animate-spin' : ''}`} />
              Refresh Stats
            </Button>
          </div>

          {archiveLoading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : archiveStats?.stats?.total_documents > 0 ? (
            <>
              {/* Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Database className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-purple-600">
                          {archiveStats?.stats?.total_documents || 0}
                        </p>
                        <p className="text-sm text-gray-500">Total Archived Documents</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <HardDrive className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-600">
                          {formatBytes(archiveStats?.stats?.total_bytes || 0)}
                        </p>
                        <p className="text-sm text-gray-500">Archive Size</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Clock className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-600">
                          {archiveStats?.archive_threshold_days || 30} days
                        </p>
                        <p className="text-sm text-gray-500">Archive Threshold</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Collections Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5" />
                    Archived Collections
                  </CardTitle>
                  <CardDescription>
                    Click on a collection to view and restore archived documents
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(archiveStats?.stats?.collections || {}).map(([collection, data]) => (
                      <div 
                        key={collection}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                        onClick={() => fetchArchivedData(collection)}
                      >
                        <div className="flex items-center gap-3">
                          <Archive className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="font-medium">{COLLECTION_NAMES[collection] || collection}</p>
                            <p className="text-sm text-gray-500">
                              {data.documents} documents • {formatBytes(data.bytes)}
                            </p>
                            {data.date_range && (
                              <p className="text-xs text-gray-400 mt-1">
                                <Calendar className="h-3 w-3 inline mr-1" />
                                {formatDate(data.date_range.oldest)} - {formatDate(data.date_range.newest)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{data.archives} archives</Badge>
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-gray-500">
                  <Archive className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <h3 className="text-lg font-medium mb-2">No Archived Data Yet</h3>
                  <p className="text-sm max-w-md mx-auto">
                    Data older than 30 days will automatically be archived here. 
                    Your current data is all recent and stored in the live database.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* View Archived Documents Dialog */}
          {selectedCollection && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    Viewing: {COLLECTION_NAMES[selectedCollection] || selectedCollection}
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setSelectedCollection('');
                        setArchivedDocuments([]);
                      }}
                    >
                      Close
                    </Button>
                    {archivedDocuments.length > 0 && (
                      <Button 
                        size="sm"
                        onClick={() => setRestoreDialogOpen(true)}
                        disabled={selectedForRestore.length === 0}
                      >
                        <RotateCcw className="mr-2 h-4 w-4" />
                        Restore Selected ({selectedForRestore.length})
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {viewingArchive ? (
                  <div className="flex items-center justify-center h-32">
                    <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
                  </div>
                ) : archivedDocuments.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Database className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No archived documents found for this collection</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {archivedDocuments.map((doc, index) => (
                      <div 
                        key={doc._id || index}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                          selectedForRestore.includes(doc._id) 
                            ? 'bg-blue-50 border-blue-300' 
                            : 'bg-gray-50 border-transparent hover:bg-gray-100'
                        }`}
                        onClick={() => {
                          if (doc._id) {
                            setSelectedForRestore(prev => 
                              prev.includes(doc._id) 
                                ? prev.filter(id => id !== doc._id)
                                : [...prev, doc._id]
                            );
                          }
                        }}
                      >
                        <input 
                          type="checkbox" 
                          checked={selectedForRestore.includes(doc._id)}
                          onChange={() => {}}
                          className="h-4 w-4"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {doc.job_number || doc.name || doc.subject || doc.id || doc._id}
                          </p>
                          <p className="text-xs text-gray-500">
                            {doc.created_at && `Created: ${formatDate(doc.created_at)}`}
                            {doc.status && ` • Status: ${doc.status}`}
                          </p>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          Archived
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Purchase Modal */}
      <Dialog open={showPurchaseModal} onOpenChange={setShowPurchaseModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Purchase Additional Storage
            </DialogTitle>
            <DialogDescription>
              Add more cloud storage to your plan. Billed monthly.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Amount (GB)</Label>
              <div className="flex gap-2 mt-2">
                {[5, 10, 25, 50, 100].map(amount => (
                  <Button
                    key={amount}
                    variant={purchaseAmount === amount ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setPurchaseAmount(amount)}
                  >
                    {amount} GB
                  </Button>
                ))}
              </div>
              <Input
                type="number"
                value={purchaseAmount}
                onChange={(e) => setPurchaseAmount(Math.max(1, parseInt(e.target.value) || 1))}
                min={1}
                max={1000}
                className="mt-2"
              />
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between text-sm">
                <span>Price per GB</span>
                <span>£{(pricing?.price_per_gb_pounds || 0.15).toFixed(2)}/month</span>
              </div>
              <div className="flex justify-between font-bold text-lg mt-2 pt-2 border-t">
                <span>Monthly Total</span>
                <span>£{(purchaseAmount * (pricing?.price_per_gb_pounds || 0.15)).toFixed(2)}</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurchaseModal(false)}>
              Cancel
            </Button>
            <Button onClick={handlePurchase}>
              Purchase {purchaseAmount} GB
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5" />
              Restore Documents
            </DialogTitle>
            <DialogDescription>
              This will restore {selectedForRestore.length} document(s) from the archive 
              back to your live database. The restored documents will be marked with a 
              &quot;restored_from_archive&quot; flag.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-gray-600">
              <strong>Collection:</strong> {COLLECTION_NAMES[selectedCollection] || selectedCollection}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Documents:</strong> {selectedForRestore.length}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRestore} disabled={restoring}>
              {restoring ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Restoring...
                </>
              ) : (
                <>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Restore Documents
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StorageManagement;
